import React, { useEffect, useState } from 'react';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import Grid from '@mui/material/Grid';
import { FormInput } from 'components/molecules/FormInput';
import { useForm } from 'react-hook-form';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { SelectedFilters } from 'components/organisms/SelectedFilters';
import { ESearchGroupFirearmFormLabelsTypes } from 'constants/SearchFormLabelsTypes';
import {
  initialGroupFirearmSearchFormValues,
  ISearchGroupFirearmFormValues,
} from 'models/form';
import { useTranslations } from 'hooks/useTranslations';
import { FormAutocomplete } from 'components/molecules/FormAutocomplete';
import { prepareFilters } from 'helpers/filters';

interface ISearchFormValues {
  serialNumber: string;
  makeUniqueId: string;
  modelUniqueId: string;
  caliberUniqueId: string;
}

interface IGroupFirearmSearchForm {
  onSubmit: (arg: ISearchGroupFirearmFormValues) => void;
}

const GroupFirearmSearchForm = ({ onSubmit }: IGroupFirearmSearchForm) => {
  const { make, model, caliber } = useLegalEntities();
  const { t } = useTranslations();
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<ISearchFormValues>({
    defaultValues: initialGroupFirearmSearchFormValues,
  });
  const [values, setValues] = useState<ISearchGroupFirearmFormValues>(
    initialGroupFirearmSearchFormValues,
  );

  useEffect(() => {
    const subscription = watch((value) => {
      setValues(value as ISearchGroupFirearmFormValues);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleResetField = (name?: string, refreshResults: boolean = true) => {
    if (name) {
      reset({ ...getValues(), [name]: '' });
    } else {
      reset(initialGroupFirearmSearchFormValues);
    }

    if (refreshResults) {
      onSubmit(getValues());
    }
  };

  return (
    <SearchWrapper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} columns={10} sx={{ justifyContent: 'space-between' }}>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <FormInput
              charsLimit={120}
              maxLength
              name="serialNumber"
              label={t('groupDetails.firearmListSection.serialNumber.label')}
              placeholder={t('groupDetails.firearmListSection.serialNumber.label')}
              setValue={setValue}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <FormAutocomplete
              options={mapDictionaryToOptionProp(make)}
              name="makeUniqueId"
              label={t('groupDetails.firearmListSection.make.label')}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <FormAutocomplete
              options={mapDictionaryToOptionProp(model)}
              name="modelUniqueId"
              label={t('groupDetails.firearmListSection.model.label')}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <FormAutocomplete
              options={mapDictionaryToOptionProp(caliber)}
              name="caliberUniqueId"
              label={t('groupDetails.firearmListSection.calibre.label')}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <Button
              variant={EButtonVariants.contained}
              size={EButtonSizes.small}
              label={t('groupDetails.firearmListSection.search.button')}
              type="submit"
              fullWidth
              sx={{ mt: 3.5 }}
            />
          </Grid>
        </Grid>
      </form>
      <SelectedFilters
        name="group_firearm"
        values={values}
        labels={ESearchGroupFirearmFormLabelsTypes}
        handleDelete={handleResetField}
        showSaveFilters={false}
        noPreset
        saveRouterStateAfterClearAll={false}
        saveQueryParams={['type']}
        dictionaries={{
          makeUniqueId: prepareFilters(make, 'uniqueId', 'name'),
          modelUniqueId: prepareFilters(model, 'uniqueId', 'name'),
          caliberUniqueId: prepareFilters(caliber, 'uniqueId', 'name'),
        }}
      />
    </SearchWrapper>
  );
};

export { GroupFirearmSearchForm };
